import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertTriangle, Download, Euro, FileCog, Loader2, Send, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUP_STATUS = {
  imported: { label: 'Importée', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  matched: { label: 'Rapprochée', cls: 'bg-green-50 text-green-700 border-green-200' },
  partial: { label: 'Partielle', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  error: { label: 'Erreur', cls: 'bg-red-50 text-red-700 border-red-200' },
};
const CLI_STATUS = {
  draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
  validated: { label: 'Validée', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  sent: { label: 'Envoyée', cls: 'bg-green-50 text-green-700 border-green-200' },
  paid: { label: 'Payée', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
};

const SupplierTab = ({ onGenerated }) => {
  const [invoices, setInvoices] = useState([]);
  const [labs, setLabs] = useState([]);
  const [form, setForm] = useState({ invoice_number: '', invoice_date: '', fabricant_id: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    const [i, l] = await Promise.all([
      axios.get(`${API}/supplier-invoices`),
      axios.get(`${API}/organizations?type=fabricant`),
    ]);
    setInvoices(i.data);
    setLabs(l.data);
  }, []);
  useEffect(() => { fetchAll().catch(() => toast.error('Erreur de chargement')); }, [fetchAll]);

  const upload = async () => {
    if (!file || !form.invoice_number || !form.fabricant_id) { toast.error('Fichier, n° de facture et laboratoire requis'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      const { data } = await axios.post(`${API}/supplier-invoices`, fd);
      toast.success(`Facture importée — ${SUP_STATUS[data.status]?.label}`);
      setFile(null); setForm({ invoice_number: '', invoice_date: '', fabricant_id: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import");
    } finally { setBusy(false); }
  };

  const generate = async (id) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/supplier-invoices/${id}/generate-client-invoices`);
      if (data.created.length) toast.success(`${data.created.length} facture(s) client générée(s) en brouillon`);
      if (data.alerts.length) toast.warning(data.alerts.map((a) => a.message).join(' · '));
      if (!data.created.length && !data.alerts.length) toast.info('Aucune nouvelle facture à générer (lignes déjà facturées)');
      fetchAll(); onGenerated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de génération');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="border-border">
        <CardHeader><CardTitle className="text-base">Importer une facture fournisseur (Excel/CSV)</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-5 gap-3 items-end">
          <div>
            <Label>N° facture</Label>
            <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="supinv-number-input" />
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
          </div>
          <div>
            <Label>Laboratoire</Label>
            <Select value={form.fabricant_id} onValueChange={(v) => setForm({ ...form, fabricant_id: v })}>
              <SelectTrigger data-testid="supinv-lab-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
              <SelectContent>{labs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fichier</Label>
            <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files[0])} data-testid="supinv-file-input" />
          </div>
          <Button onClick={upload} disabled={busy} className="gap-2" data-testid="supinv-upload-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Importer
          </Button>
        </CardContent>
      </Card>

      {invoices.map((inv) => {
        const s = SUP_STATUS[inv.status] || SUP_STATUS.imported;
        const matched = inv.parsed_lines.filter((l) => l.matched_order_id).length;
        return (
          <Card key={inv.id} className="border-border" data-testid={`supinv-row-${inv.invoice_number}`}>
            <CardContent className="py-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold text-sm">{inv.invoice_number} <span className="text-muted-foreground font-normal">· {inv.fabricant_name} · {inv.currency}</span></p>
                  <p className="text-xs text-muted-foreground">{matched}/{inv.parsed_lines.length} ligne(s) rapprochée(s)</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={s.cls}>{s.label}</Badge>
                  {matched > 0 && (
                    <Button size="sm" onClick={() => generate(inv.id)} disabled={busy} className="gap-1" data-testid={`supinv-generate-${inv.invoice_number}`}>
                      <FileCog className="h-4 w-4" />Générer facture(s) client
                    </Button>
                  )}
                </div>
              </div>
              {inv.alerts?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {inv.alerts.map((a, i) => (
                    <p key={i} className="text-xs text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3 shrink-0" />{a.message}
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const ClientTab = ({ refreshKey }) => {
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ paid_at: '', amount_received: '' });

  const fetchAll = useCallback(async () => {
    const { data } = await axios.get(`${API}/client-invoices`);
    setInvoices(data);
  }, []);
  useEffect(() => { fetchAll().catch(() => {}); }, [fetchAll, refreshKey]);

  const act = async (id, action) => {
    setBusy(true);
    try {
      await axios.post(`${API}/client-invoices/${id}/${action}`);
      toast.success(action === 'validate' ? 'Facture validée — PDF généré'
        : action === 'unmark-paid' ? 'Encaissement annulé' : 'Facture envoyée au cabinet');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  const openPay = (inv) => {
    setPayForm({ paid_at: new Date().toISOString().slice(0, 10), amount_received: inv.total.toFixed(2) });
    setPayTarget(inv);
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/client-invoices/${payTarget.id}/mark-paid`, {
        paid_at: payForm.paid_at,
        amount_received: parseFloat(payForm.amount_received),
      });
      toast.success('Encaissement enregistré');
      setPayTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      {invoices.length === 0 && <Card className="border-dashed"><CardContent className="py-8 text-center text-muted-foreground">Aucune facture client.</CardContent></Card>}
      {invoices.map((inv) => {
        const s = CLI_STATUS[inv.status] || CLI_STATUS.draft;
        return (
          <Card key={inv.id} className="border-border" data-testid={`cliinv-row-${inv.invoice_number}`}>
            <CardContent className="py-3 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-semibold text-sm">{inv.invoice_number} <span className="text-muted-foreground font-normal">· {inv.cabinet_name}</span></p>
                <p className="text-xs text-muted-foreground">{inv.lines.length} ligne(s) · HT {inv.subtotal.toFixed(2)} € · TTC <b>{inv.total.toFixed(2)} €</b></p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={s.cls}>{s.label}</Badge>
                {inv.status === 'draft' && (
                  <Button size="sm" onClick={() => act(inv.id, 'validate')} disabled={busy} data-testid={`cliinv-validate-${inv.invoice_number}`}>Valider</Button>
                )}
                {inv.status === 'validated' && (
                  <Button size="sm" onClick={() => act(inv.id, 'send')} disabled={busy} className="gap-1" data-testid={`cliinv-send-${inv.invoice_number}`}>
                    <Send className="h-4 w-4" />Envoyer
                  </Button>
                )}
                {(inv.status === 'validated' || inv.status === 'sent') && (
                  <Button size="sm" variant="outline" onClick={() => openPay(inv)} disabled={busy} className="gap-1 text-sky-700 border-sky-300" data-testid={`cliinv-pay-${inv.invoice_number}`}>
                    <Euro className="h-4 w-4" />Encaisser
                  </Button>
                )}
                {inv.status === 'paid' && (
                  <>
                    <span className="text-xs text-muted-foreground">Encaissée le {inv.paid_at} · {inv.amount_received?.toFixed(2)} €</span>
                    <Button size="sm" variant="ghost" onClick={() => act(inv.id, 'unmark-paid')} disabled={busy} className="text-red-600 text-xs" data-testid={`cliinv-unpay-${inv.invoice_number}`}>
                      Annuler l'encaissement
                    </Button>
                  </>
                )}
                {inv.pdf_path && (
                  <a href={`${API}/client-invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer">
                    <Button size="icon" variant="ghost" data-testid={`cliinv-pdf-${inv.invoice_number}`}><Download className="h-4 w-4" /></Button>
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Encaisser la facture {payTarget?.invoice_number}</DialogTitle>
            <DialogDescription>
              L'encaissement réel alimente le calcul de répartition entre associés.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1">
              <Label>Date d'encaissement</Label>
              <Input type="date" value={payForm.paid_at} onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} data-testid="mark-paid-date-input" />
            </div>
            <div className="space-y-1">
              <Label>Montant encaissé (€)</Label>
              <Input type="number" step="0.01" value={payForm.amount_received} onChange={(e) => setPayForm({ ...payForm, amount_received: e.target.value })} data-testid="mark-paid-amount-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayTarget(null)}>Annuler</Button>
            <Button onClick={markPaid} disabled={busy} data-testid="mark-paid-confirm-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enregistrer l'encaissement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function AdminInvoicing() {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <AdminLayout title="Facturation" subtitle="Factures fournisseur → factures client automatisées">
      <Tabs defaultValue="supplier">
        <TabsList>
          <TabsTrigger value="supplier" data-testid="tab-supplier">Factures fournisseur</TabsTrigger>
          <TabsTrigger value="client" data-testid="tab-client">Factures client</TabsTrigger>
        </TabsList>
        <TabsContent value="supplier"><SupplierTab onGenerated={() => setRefreshKey((k) => k + 1)} /></TabsContent>
        <TabsContent value="client"><ClientTab refreshKey={refreshKey} /></TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
